export type Element = { Name: string; Symbol: string };

export type PowderInventoryLot = {
    Description: string;
    Lot: string;
    Qty: number;
    Rvid: string;
    Trim:string;
    HasComposition: boolean;
};

export type PowderInventoryGroupedByDescription = {
    Description: string;
    Lots: PowderInventoryLot[];
};

export type PowderComposition = {
    Symbol: string;
    Percentage: number;
};

export type SinteringTemperatureRow = {
    Code: string;
    Temperature: string;
    Grade?: string;
    UnderLayer?: string;
    Remarks?: string;
};

export type RawMaterialCalculationMaterial =
    | { role: "main"; lot: string; raw_mtrl: string }
    | { role: "trim"; trim_element: "W" | "C"; lot: string; raw_mtrl: string };


export type RawMaterialRowPayload= {
    role: "main" | "trim";
    lot: string;
    raw_mtrl: string;
    trim_element?:string;
};

export type RawMaterialCalculationPayload = {
    total_weight: number;
    weight_unit: "kg" | "g";
    selected_elements: string[];
    targets_pct: Record<string, number>;
    balance_element: string;
    lump_impurities_into: string;
    materials: RawMaterialRowPayload[];
    prz: {
        enabled: boolean;
        selected_lot?: string;
        ratio_pct?: number;
    };
};

export type SolveTableRow = {
    ratioPct?: number;
    [key: string]: number | undefined;
};

export type SolveRatioAvailabilityDetail = {
    lot: string;
    weight: number;
    avail_g: number;
    limit_total_g: number;
};

export type SolveRatioAvailabilityPrecheckItem = {
    lots: string[];
    weights: number[];
    max_total_g: number;
    details: SolveRatioAvailabilityDetail[];
};

export type SolveExcludedLotDueToImpurities = {
    lot: string;
    non_target_sum_pct: number;
    top_non_target: Array<[string, number]>;
};

export type SolveFixedRatioActual = {
    lots: string[];
    weights: number[];
    grams: number[];
    group_total_g: number;
    actual_shares: number[];
};

export type SolveDiagnosticsAttempt = {
    name: string;
    success: boolean;
    status: string;
};

export type SolveInfeasibilityAvailability = {
    batch_g?: number;
    total_max_available_g?: number;
    total_min_forced_g?: number;
};

export type SolveInfeasibilityElementConstraint = {
    element?: string;
    target_pct?: number;
    isolated_min_pct?: number;
    isolated_max_pct?: number;
    isolated_feasible?: boolean;
    reason?: string;
};

export type SolveInfeasibilityDiagnostics = {
    summary?: string;
    reasons?: string[];
    trim_note?: string;
    prz_constraint?: string | null;
    availability?: SolveInfeasibilityAvailability;
    element_constraints?: SolveInfeasibilityElementConstraint[];
};

export type SolveDiagnostics = {
    attempts?: SolveDiagnosticsAttempt[];
    likely_causes?: string[];
    smallest_availability_bounds_g?: Array<[string, number]>;
    infeasibility?: SolveInfeasibilityDiagnostics;
};

export type SolveIncludedElementsMatrixRow = {
    role?: "main" | "trim";
    trim_element?: string | null;
    lot?: string | number | null;
    raw_mtrl?: string | number | null;
    ratio_pct?: number;
    contrib_pct?: Record<string, number>;
};

export type SolveIncludedElementsMatrix = {
    columns?: string[];
    rows?: SolveIncludedElementsMatrixRow[];
};

export type ImpurityExpansionElement = {
    element?: string;
    fraction_in_lot?: number;
    contribution_g?: number;
    contribution_to_batch_pct?: number;
};

export type SolveImpurityExpansionMaterial = {
    role?: "main" | "trim";
    trim_element?: string | null;
    lot?: string | number | null;
    raw_material?: string | number | null;
    raw_mtrl?: string | number | null;
    lumped_into?: string;
    non_selected_elements?: ImpurityExpansionElement[];
};

export type SolveImpuritiesExpansion = {
    lumped_into?: string;
    per_material?: SolveImpurityExpansionMaterial[];
};

export type SolveRawMaterialResult = {
    success?: boolean;
    message?: string;
    solver_build?: string;
    weight_unit?: string;
    likely_causes?: string[];
    diagnostics?: SolveDiagnostics;
    batch_g?: number;
    inputs_echo?: {
        total_weight?: number;
        weight_unit?: string;
        selected_elements?: string[];
        targets_pct_input?: Record<string, number>;
        targets_pct_full?: Record<string, number>;
        balance_element?: string;
        lump_impurities_into?: string;
    };
    solution?: {
        materials?: Array<{
            role?: "main" | "trim";
            trim_element?: string | null;
            lot?: string;
            raw_material?: string;
            weight_g?: number;
            ratio_pct?: number;
        }>;
        achieved_pct?: Record<string, number>;
        deviation_pct_points?: Record<string, number>;
    };
    solver?: {
        method?: string;
        status?: string;
    };
    ratio_availability_precheck?: SolveRatioAvailabilityPrecheckItem[];
    excluded_lots_due_to_impurities?: SolveExcludedLotDueToImpurities[];
    fixed_ratio_actuals?: SolveFixedRatioActual[];
    included_elements_matrix?: SolveIncludedElementsMatrix;
    impurities_expansion?: SolveImpuritiesExpansion;
};

export type SolveRawMaterialError = {
    success?: false;
    message?: string;
    detail?: string;
    statusCode?: number;
    solver_build?: string;
    infeasibility_reason?: string;
    diagnostics?: SolveDiagnostics;
};

export type SolveRawMaterialResponse = SolveRawMaterialResult | SolveRawMaterialError;

export const isSolveRawMaterialSuccessResponse = (
    response: SolveRawMaterialResponse | null
): response is SolveRawMaterialResult => response?.success === true;

export type SolveMappedRecords = {
    achievedPct: Record<string, number>;
    recipeByLot: Record<string, number>;
    recipeByMaterial: Record<string, number>;
    tableByLot: Record<string, SolveTableRow>;
    tableByMaterial: Record<string, SolveTableRow>;
};

const normalizeKey = (value: string): string => value.trim().toLowerCase();

export const mapSolveResultToRecords = (result: SolveRawMaterialResult): SolveMappedRecords => {
    const recipeByLot: Record<string, number> = {};
    const recipeByMaterial: Record<string, number> = {};
    const tableByLot: Record<string, SolveTableRow> = {};
    const tableByMaterial: Record<string, SolveTableRow> = {};

    (result.solution?.materials ?? []).forEach((item) => {
        const lot = String(item.lot ?? "").trim();
        const material = String(item.raw_material ?? "").trim();
        const weightG = item.weight_g;
        const ratioPct = item.ratio_pct;

        if (typeof weightG === "number" && Number.isFinite(weightG)) {
            if (lot) recipeByLot[normalizeKey(lot)] = weightG;
            if (material) recipeByMaterial[normalizeKey(material)] = weightG;
        }

        const derivedRow: SolveTableRow = {
            ratioPct: typeof ratioPct === "number" && Number.isFinite(ratioPct) ? ratioPct : undefined,
        };

        if (lot) tableByLot[normalizeKey(lot)] = derivedRow;
        if (material) tableByMaterial[normalizeKey(material)] = derivedRow;
    });

    (result.included_elements_matrix?.rows ?? []).forEach((item) => {
        const lot = String(item.lot ?? "").trim();
        const material = String(item.raw_mtrl ?? "").trim();
        const ratioPct = item.ratio_pct;
        const contribPct = item.contrib_pct ?? {};

        const seedRow = (lot ? tableByLot[normalizeKey(lot)] : undefined)
            ?? (material ? tableByMaterial[normalizeKey(material)] : undefined)
            ?? {};

        const mergedRow: SolveTableRow = {
            ...seedRow,
            ratioPct: typeof ratioPct === "number" && Number.isFinite(ratioPct)
                ? ratioPct
                : seedRow.ratioPct,
        };

        Object.entries(contribPct).forEach(([symbol, value]) => {
            if (typeof value === "number" && Number.isFinite(value)) {
                mergedRow[symbol] = value;
            }
        });

        if (lot) tableByLot[normalizeKey(lot)] = mergedRow;
        if (material) tableByMaterial[normalizeKey(material)] = mergedRow;
    });

    return {
        achievedPct: result.solution?.achieved_pct ?? {},
        recipeByLot,
        recipeByMaterial,
        tableByLot,
        tableByMaterial,
    };
};

export const getMappedTableRowForMaterialLot = (
    material: string,
    lot: string,
    mapped: SolveMappedRecords
): SolveTableRow | undefined => {
    return mapped.tableByLot[normalizeKey(lot)] ?? mapped.tableByMaterial[normalizeKey(material)];
};

export const getMappedRecipeWeightForMaterialLot = (
    material: string,
    lot: string,
    mapped: SolveMappedRecords
): number | null => {
    return mapped.recipeByLot[normalizeKey(lot)]
        ?? mapped.recipeByMaterial[normalizeKey(material)]
        ?? null;
};

export const getTableRowForMaterialLot = (
    material: string,
    lot: string,
    result: SolveRawMaterialResult
): SolveTableRow | undefined => {
    const mapped = mapSolveResultToRecords(result);
    return getMappedTableRowForMaterialLot(material, lot, mapped);
};

export const getRecipeWeightForMaterialLot = (
    material: string,
    lot: string,
    result: SolveRawMaterialResult
): number | null => {
    const mapped = mapSolveResultToRecords(result);
    return getMappedRecipeWeightForMaterialLot(material, lot, mapped);
};

export const GetElements = async (): Promise<Element[] | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
        const response = await fetch(`${apiUrl}/teams/powderorder/elements`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            }
        });
        if (response.ok) {
            const result: Element[] = await response.json();
            return result;
        } else {
            console.error("Failed to fetch elements", response.status, response.statusText);
            return null;
        }
    } catch (error) {
        console.error("Error fetching elements:", error);
        return null;
    }
};

export const GetInventoryGroupedByDescription = async (): Promise<PowderInventoryGroupedByDescription[] | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const endpoint = `${apiUrl}/teams/powderorder/inventory/grouped/description`;
    try {
        const response = await fetch(endpoint, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            }
        });

        if(response.ok) {
            console.log("Fetched grouped powder inventory successfully");
            console.log("Response data:", await response.clone().json());
        }

        if (response.ok) {
            const result: PowderInventoryGroupedByDescription[] = await response.json();
            return result;
        }

        console.error("Failed to fetch grouped powder inventory", response.status, response.statusText);
        return null;
    } catch (error) {
        console.error("Error fetching grouped powder inventory:", error);
        return null;
    }
};

export const GetSinteringTemperatures = async (): Promise<SinteringTemperatureRow[] | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const endpoint = `${apiUrl}/teams/powderorder/sintering`;

    try {
        const response = await fetch(endpoint, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            }
        });

        if (!response.ok) {
            console.error("Failed to fetch sintering temperatures", response.status, response.statusText);
            return null;
        }

        const parsedRows: SinteringTemperatureRow[] = await response.json();
        return parsedRows;
    } catch (error) {
        console.error("Error fetching sintering temperatures:", error);
        return null;
    }
}

export const GetComposition = async (rvid: string, lot: string): Promise<PowderComposition[] | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const endpoint = `${apiUrl}/teams/powderorder/composition?rvid=${encodeURIComponent(rvid)}&lot=${encodeURIComponent(lot)}`;

    try {
        const response = await fetch(endpoint, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            }
        });

        if (response.ok) {
            const result: PowderComposition[] = await response.json();
            return result;
        }

        console.error("Failed to fetch powder composition", response.status, response.statusText);
        return null;
    } catch (error) {
        console.error("Error fetching powder composition:", error);
        return null;
    }
};

export const SolveRawMaterialCalculation = async (payload: RawMaterialCalculationPayload): Promise<SolveRawMaterialResponse | null> => {
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
    const endpoint = `${apiUrl}/oi/powderAnalysis/solve`;

    const parseSolveResponse = (raw: string): SolveRawMaterialResponse | null => {
        let current: unknown = raw;

        // Some backends return JSON encoded as a string (sometimes multiple times).
        // Keep decoding while value is a JSON string.
        for (let i = 0; i < 3; i += 1) {
            if (typeof current !== "string") break;

            const trimmed = current.trim();
            if (!trimmed) return null;

            try {
                current = JSON.parse(trimmed);
            } catch {
                // Plain text (non-JSON) response
                return { success: false, message: trimmed };
            }
        }

        if (typeof current === "object" && current !== null) {
            return current as SolveRawMaterialResponse;
        }

        if (typeof current === "string") {
            return { success: false, message: current };
        }

        return null;
    };

    try {
        const response = await fetch(endpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });

        const responseText = await response.text();
        const parsed = parseSolveResponse(responseText);

        if (parsed) {
            console.log("SolveRawMaterialResult:", parsed);
        }

        if (response.ok) {
            return parsed;
        }

        console.error("Failed to solve raw material calculation", response.status, response.statusText, endpoint, responseText);
        return (parsed && typeof parsed === 'object') ? parsed : {
            success: false,
            message: `HTTP ${response.status} ${response.statusText}`,
            statusCode: response.status,
        };
    } catch (error) {
        console.error("Error solving raw material calculation:", endpoint, error);
        return null;
    }
};

